import {
  Box,
  Typography,
  CircularProgress,
  Hidden,
  Divider,
  Link as LinkMaterial,
} from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import HorizontalScroll from 'react-scroll-horizontal';
import Head from '../../../components/Head';
import Layout from '../../../layout';
import { FleetsService } from '../../../services';
import { formatMoney } from '../../../utils/formatters/formatMoney';
import {
  enTranslateColor,
  enTranslateFuel,
  enTranslateSteering,
  enTranslateTransmission,
} from '../../../services/models/car';
import { useCars } from '../hooks/useCars';

import { sanitize } from 'dompurify';

import useStyles from './styles';
import { assetUrl } from '../../../utils';

interface accessories {
  id: number;
  name: string;
}

interface picture {
  id: number;
  image: string;
}

export interface carProps {
  id: number;
  name: string;
  brand: string;
  color: string;
  fuel: string;
  steering: string;
  observations: string;
  km: number;
  price: string;
  fipePrice: string;
  observation: string;
  thumbnail: string;
  licensePlate: string;
  accessories: accessories[];
  vehiclePicture: picture[];
  firstDocument: string;
  secondDocument: string;
  year: number;
  yearModel: number;
  ports: number;
  transmission: string;
}

const CarDetails = () => {
  const classes = useStyles();
  const { setCarSelected, carSelected, hasOrder } = useCars();
  const [car, setCar] = useState<carProps>({} as carProps);
  const [loading, setLoading] = useState(false);
  const [hasCarIncluded, setHasCarIncluded] = useState(false);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    (async () => {
      setLoading((prevState) => !prevState);
      const { data } = await FleetsService.listById(id);
      const content = {
        ...data,
      };
      setCar(content);
      setLoading((prevState) => !prevState);
    })();
  }, [id]);

  useEffect(() => {
    const hasCarIncluded = carSelected.find((c) => c.id === car.id);
    if (hasCarIncluded) {
      setHasCarIncluded((prevState) => !prevState);
      return;
    }
  }, [carSelected, car.id]);

  const handleCarSelected = useCallback(
    (car: carProps) => {
      const cars: carProps[] = hasOrder ? [...carSelected, car] : [car];
      setCarSelected(cars);
    },
    [carSelected, hasOrder, setCarSelected]
  );

  return (
    <Layout page="fleetsCarDescription">
      {loading ? (
        <Box marginTop={20}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Hidden smDown>
            {!!carSelected.length && (
              <Box
                display="flex"
                justifyContent="space-between"
                className={classes.ToolTip}
              >
                <p className={classes.text}>
                  Você se interessou por {`${carSelected.length}`} veículo(s).
                </p>
                <Link to="/frotas/veiculos-selecionados">
                  <p className={classes.textActive}>Ver informações</p>
                </Link>
              </Box>
            )}
          </Hidden>
          <Hidden smUp>
            {!!carSelected.length && (
              <Link to="/frotas/veiculos-selecionados">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  className={classes.ToolTip}
                >
                  <p className={classes.text}>
                    <strong>{`${carSelected.length}`}</strong> veículo(s)
                    selecinados.
                  </p>
                </Box>
              </Link>
            )}
          </Hidden>
          <Head>Descrição do veículo</Head>
          <Box className={classes.container}>
            <Box className={classes.containerImages} display="flex">
              <Hidden xsDown={true} implementation="js">
                <HorizontalScroll>
                  {car.vehiclePicture?.map((picture) => (
                    <img
                      key={picture.id}
                      className={classes.img}
                      src={assetUrl(picture.image)}
                      alt="Veículo"
                      onError={function (e) {
                        (e.target as any).style.display = 'none';
                      }}
                    />
                  ))}
                </HorizontalScroll>
              </Hidden>
              <Hidden smUp={true}>
                {car.vehiclePicture?.map((picture) => (
                  <img
                    key={picture.id}
                    className={classes.img}
                    src={assetUrl(picture.image)}
                    alt="Veículo"
                  />
                ))}
              </Hidden>
            </Box>
            <Box className={classes.containerDetails}>
              <Box className={classes.contentDetails} display="flex">
                <Box className={classes.header}>
                  <Box display="flex" alignItems="baseline">
                    <strong className={classes.brandCar}>{car.brand}</strong>
                    <Typography className={classes.nameCar} variant="h6">
                      {car.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography className={classes.nameCar}>
                      {formatMoney(car.price as any)}
                    </Typography>
                    <Typography className={classes.fipePrice}>
                      {car.fipePrice && (
                        <p>FIPE {formatMoney(car.fipePrice as any)}</p>
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Hidden smUp>
                  <Divider />
                </Hidden>
                <Box className={classes.main} justifyContent="space-between">
                  <Box>
                    <Typography className={classes.comonTitle}>Ano</Typography>
                    <strong>
                      {car.yearModel}/{car.year}
                    </strong>
                  </Box>
                  <Box>
                    <Typography className={classes.comonTitle}>KM</Typography>
                    <strong>{car.km}</strong>
                  </Box>
                  <Box>
                    <Typography className={classes.comonTitle}>
                      Câmbio
                    </Typography>
                    <strong>
                      {
                        enTranslateTransmission[
                          car.transmission as keyof typeof enTranslateTransmission
                        ]
                      }
                    </strong>
                  </Box>
                </Box>
                <Box className={classes.main} justifyContent="space-between">
                  <Box>
                    <Typography className={classes.comonTitle}>
                      Final da placa
                    </Typography>
                    <strong>{car.licensePlate}</strong>
                  </Box>
                  <Box>
                    <Typography className={classes.comonTitle}>Cor</Typography>
                    <strong>
                      {
                        enTranslateColor[
                          car.color as keyof typeof enTranslateColor
                        ]
                      }
                    </strong>
                  </Box>
                  <Box>
                    <Typography className={classes.comonTitle}>
                      Portas
                    </Typography>
                    <strong>{car.ports}</strong>
                  </Box>
                </Box>
                <Box className={classes.main} justifyContent="space-between">
                  <Box>
                    <Typography className={classes.comonTitle}>
                      Direção
                    </Typography>
                    <strong>
                      {
                        enTranslateSteering[
                          car.steering as keyof typeof enTranslateSteering
                        ]
                      }
                    </strong>
                  </Box>
                  <Box>
                    <Typography className={classes.comonTitle}>
                      Combustível
                    </Typography>
                    <strong>
                      {
                        enTranslateFuel[
                          car.fuel as keyof typeof enTranslateFuel
                        ]
                      }
                    </strong>
                  </Box>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  className={classes.main}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography className={classes.comonTitle}>
                      Observações
                    </Typography>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitize(car.observations),
                      }}
                    />
                  </Box>
                  {car.firstDocument && (
                    <LinkMaterial href={assetUrl(car.firstDocument)} target="_blank">
                      <p>Vistoria cautelar</p>
                    </LinkMaterial>
                  )}
                  {car.secondDocument && (
                    <LinkMaterial href={assetUrl(car.secondDocument)} target="_blank">
                      <p>Vistoria de constatação</p>
                    </LinkMaterial>
                  )}
                </Box>
              </Box>
              <Hidden smUp>
                <Divider />
              </Hidden>
              <Box className={classes.accessories}>
                <button
                  type="button"
                  className={classes.button}
                  disabled={hasCarIncluded}
                  onClick={() => handleCarSelected(car)}
                >
                  {hasCarIncluded ? 'Carro selecionado' : 'Quero este Carro'}
                </button>
                <Typography className={classes.comonTitle}>
                  Acessórios e equipamentos
                </Typography>
                <Box className={classes.accessoriesContent}>
                  {car.accessories?.map((accessorie) => (
                    <strong className={classes.strong} key={accessorie.id}>
                      {accessorie.name}
                    </strong>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Layout>
  );
};

export default CarDetails;
